import { useRef } from "react";

export type Tab = { id: string; label: string; count?: number };

/**
 * The chart's sections, as a tab strip.
 *
 * Brief 03's board organises the chart into tabs and the screen ran it all
 * end to end with an index at the top. Both work; the tabs win on a long
 * record, where "scroll until you find the measurements" is the thing the
 * index existed to avoid in the first place.
 *
 * A real tab widget rather than links that look like one. A clinician reads
 * this screen with a keyboard as often as a mouse, so: arrows move between
 * tabs, Home and End jump to the ends, and only the selected tab is in the
 * tab order, which is what a screen reader expects of a tablist and what
 * stops six tabs costing six presses to get past.
 *
 * What this does NOT hold is the allergy rail or the recall. Section 7 is
 * explicit that allergies must not disappear because the clinician moved
 * toward prescribing, and a tab is exactly that disappearance. They stay in
 * the rail beside this, visible whichever section is open.
 */
export function TabBar({
  tabs,
  active,
  onChange,
  label,
}: {
  tabs: Tab[];
  active: string;
  onChange: (id: string) => void;
  label: string;
}) {
  const strip = useRef<HTMLDivElement | null>(null);

  function move(to: number) {
    const at = (to + tabs.length) % tabs.length;
    const next = tabs[at];
    if (!next) return;
    onChange(next.id);
    // Focus follows selection, which is the automatic-activation pattern a
    // tablist is expected to use when switching costs nothing.
    const buttons = strip.current?.querySelectorAll("button");
    buttons?.[at]?.focus();
  }

  function onKey(event: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (event.key) {
      case "ArrowRight":
        event.preventDefault();
        move(index + 1);
        break;
      case "ArrowLeft":
        event.preventDefault();
        move(index - 1);
        break;
      case "Home":
        event.preventDefault();
        move(0);
        break;
      case "End":
        event.preventDefault();
        move(tabs.length - 1);
        break;
    }
  }

  return (
    <div className="tabbar" role="tablist" aria-label={label} ref={strip}>
      {tabs.map((tab, index) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`panel-${tab.id}`}
            tabIndex={selected ? 0 : -1}
            className={selected ? "tab here" : "tab"}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => onKey(event, index)}
          >
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="tab-count">{tab.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** The panel for one tab. Rendered only when its tab is the open one. */
export function TabPanel({
  id,
  active,
  children,
}: {
  id: string;
  active: string;
  children: React.ReactNode;
}) {
  if (id !== active) return null;
  return (
    <div role="tabpanel" id={`panel-${id}`} aria-labelledby={`tab-${id}`} tabIndex={0}>
      {children}
    </div>
  );
}
